import PropTypes from "prop-types";
import React, { Fragment, useEffect } from "react";
import { connect } from "react-redux";
import "./wordpress-style.min.css";
import "./singleBlog.css";

import {
  getDateStringAsMonthYear,
  replaceLinksInBlog,
} from "../../utils/utilityFunctions";
import { Link, useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import { removeUnicode } from "../../utils/utilityFunctions";
import YouTubeFacade from "../YouTubeFacade/YouTubeFacade";
import parse from "html-react-parser";

import { makeStyles } from "tss-react/mui";
import { Typography } from "@mui/material";
import { getSingleBlogPostSchema } from "../../schema/schema";

const useStyles = makeStyles()((theme) => ({
  singleBlogDiv: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    [theme.breakpoints.down("md")]: {
      flexDirection: "column",
    },
  },
  blogPostContainer: {
    maxWidth: 760,
    padding: 20,
  },
  postContent: {},
  titleText: {
    "&.MuiTypography-h1": {
      textDecoration: "none",
      fontWeight: "bold",
      textAlign: "left",
      fontSize: 32,
    },
  },
  dateText: {
    width: "100%",
    fontSize: 15,
    color: "#757575",
    paddingBottom: theme.spacing(1),
  },
  breadcrumbDiv: {
    display: "inline-flex",
    alignItems: "start",
    justifyContent: "left",
    width: "100%",
    margin: "30px 0",
  },
  homeText: {
    paddingRight: 5,
  },
  relatedPostsContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  relatedPostBlock: {
    boxSizing: "border-box",
    border: "1px solid #eee",
    borderRadius: 8,
    overflow: "hidden",
    background: "#fafafa",
    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
    display: "flex",
    flexDirection: "column",
    width: "calc(50% - 30px)",
    [theme.breakpoints.down("md")]: {
      width: "100%",
      marginLeft: 0,
      marginRight: 0,
    },
  },
}));

// Utility to strip HTML tags and truncate text
function getExcerptText(html, maxLength = 200) {
  if (!html) return "";
  // Remove HTML tags
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  let text = tmp.textContent || tmp.innerText || "";
  if (text.length > maxLength) {
    return text.slice(0, maxLength) + "...";
  }
  return text;
}

// Utility to decode HTML entities
function decodeHtmlEntities(str) {
  if (!str) return "";
  const txt = document.createElement("textarea");
  txt.innerHTML = str;
  return txt.value;
}

// Utility to extract YouTube video ID from URL
function getYouTubeVideoId(url) {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return match && match[2].length === 11 ? match[2] : null;
}

export const SingleBlog = (props) => {
  const { classes } = useStyles();
  let { blogPostData, relatedPosts = [] } = props;

  // Remove YouTube embed replacement from useEffect
  useEffect(() => {
    if (blogPostData && blogPostData.content) {
      // SET IMAGE SRC - from data-src (only for images with data-src)
      let blogPostNode = document.getElementById("blogPost");
      let images = blogPostNode.querySelectorAll("img[data-src]");
      images.forEach((image, index) => {
        image.src = image.dataset.src;
        image.referrerPolicy = "no-referrer";
        image.decoding = "async";
        if (index === 0) {
          image.fetchPriority = "high";
        } else {
          image.loading = "lazy";
        }
      });
      // SET IFRAME SRC - data-src (for non-YouTube iframes)
      let iframes = blogPostNode.querySelectorAll(
        "iframe:not([src*='youtube.com']):not([src*='youtube-nocookie.com'])"
      );
      iframes.forEach((iframe) => {
        iframe.src = iframe.dataset.src;
        iframe.loading = "lazy";
      });
      // Replace links in table of content to use "#" instead of "/#"
      let tocLinks = blogPostNode.querySelectorAll(".ez-toc-link");
      tocLinks.forEach((link) => {
        let href = link.getAttribute("href");
        if (href.includes("/#")) {
          href = href.replace("/#", "#");
          link.setAttribute("href", href);
        }
      });
    }
  }, [blogPostData]);

  // Transform function for html-react-parser
  const transform = (node) => {
    if (
      node.name === "iframe" &&
      node.attribs &&
      ((node.attribs.src && node.attribs.src.includes("youtube.com")) ||
        (node.attribs["data-src"] &&
          node.attribs["data-src"].includes("youtube.com")))
    ) {
      const src = node.attribs["data-src"] || node.attribs.src;
      const videoId = getYouTubeVideoId(src);
      if (videoId) {
        return (
          <YouTubeFacade
            videoId={videoId}
            title={node.attribs.title || "YouTube video"}
            maxHeight={495}
          />
        );
      }
    }
  };

  if (!blogPostData || !blogPostData.content) {
    return null;
  }

  const blogPostSchema = getSingleBlogPostSchema({
    ...blogPostData,
    title: removeUnicode(blogPostData.title),
    description: removeUnicode(blogPostData.description),
  });

  return (
    <Fragment>
      {blogPostData && blogPostData.content ? (
        <div className={classes.singleBlogDiv}>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(blogPostSchema),
            }}
          />
          <div id={"blogPost"} className={classes.blogPostContainer}>
            <div className={classes.breadcrumbDiv}>
              <Typography
                component={Link}
                to={"/"}
                className={classes.homeText}
              >
                Home {">> "}
              </Typography>
              <Typography
                component={Link}
                to={"/blog"}
                className={classes.homeText}
              >
                Blogs {">> "}
              </Typography>
              <Typography>{removeUnicode(blogPostData.title)}</Typography>
            </div>
            <Typography className={classes.titleText} variant="h1">
              {removeUnicode(blogPostData.title)}
            </Typography>
            <Typography
              className={`${classes.dateText}`}
              dangerouslySetInnerHTML={{
                __html: getDateStringAsMonthYear(blogPostData.date),
              }}
            />
            <Typography
              component="div"
              gutterBottom
              className={`${classes.postContent} blog-post-content`}
            >
              {parse(replaceLinksInBlog(blogPostData.content), {
                replace: transform,
              })}
            </Typography>
            {relatedPosts && relatedPosts.length > 0 && (
              <div style={{ marginTop: 40 }}>
                <Typography
                  variant="h5"
                  style={{ fontWeight: "bold", marginBottom: 16 }}
                >
                  Related Posts
                </Typography>
                <div className={classes.relatedPostsContainer}>
                  {relatedPosts.map((post) => {
                    return (
                      <div key={post.slug} className={classes.relatedPostBlock}>
                        <Link
                          to={`/blog/${post.slug}`}
                          style={{ textDecoration: "none", color: "inherit" }}
                        >
                          {post.featuredImage && (
                            <img
                              src={post.featuredImage}
                              alt={decodeHtmlEntities(
                                removeUnicode(post.title)
                              )}
                              style={{
                                width: "100%",
                                height: 196,
                                objectFit: "cover",
                                display: "block",
                              }}
                            />
                          )}
                          <div style={{ padding: 16 }}>
                            <Typography
                              variant="subtitle2"
                              style={{
                                color: "#757575",
                                fontSize: 13,
                                marginBottom: 4,
                              }}
                            >
                              {post.date &&
                                new Date(post.date).toLocaleDateString(
                                  undefined,
                                  {
                                    year: "numeric",
                                    month: "short",
                                    day: "numeric",
                                  }
                                )}
                            </Typography>
                            <Typography
                              variant="h6"
                              style={{
                                fontWeight: 600,
                                fontSize: 18,
                                marginBottom: 8,
                              }}
                            >
                              {decodeHtmlEntities(removeUnicode(post.title))}
                            </Typography>
                            <Typography
                              variant="body2"
                              style={{ color: "#444", fontSize: 15 }}
                            >
                              {decodeHtmlEntities(
                                getExcerptText(post.excerpt, 200)
                              )}
                            </Typography>
                          </div>
                        </Link>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <Sidebar />
        </div>
      ) : null}
    </Fragment>
  );
};

SingleBlog.propTypes = {};

const mapStateToProps = (state) => ({});

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(SingleBlog);
